import { useState } from "react";
import { toast } from "sonner";
import { Scissors } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { num, rp } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { todayWib } from "@/components/PeriodPicker";

export default function RijekButton({ wid, productId, productName, setorEfektif, onDone, compact = true }) {
  const { isEditor } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [qty, setQty] = useState("");
  const [date, setDate] = useState(todayWib());
  const [notes, setNotes] = useState("");
  const [repairable, setRepairable] = useState(true);
  if (!isEditor) return null;
  const n = parseInt(qty || 0);
  const over = setorEfektif != null && n > setorEfektif;
  const submit = async () => {
    if (n <= 0) return toast.error("Jumlah rijek harus lebih dari 0");
    if (over) return toast.error(`Jumlah rijek melebihi setor efektif ${num(setorEfektif)} PCS`);
    setBusy(true);
    try {
      const { data } = await api.post("/transactions", { worker_id: wid, transaction_type: "RIJEK", transaction_date: date, product_id: productId, quantity: n, rijek_repairable: repairable, notes: notes || null });
      toast.success(`RIJEK ${data.transaction_id}: ${num(n)} PCS ${productId} ${repairable ? "→ perbaikan (bisa dikembalikan)" : "→ AFKIR (bon barang)"} · potongan ${rp(-(data.amount || 0))}`);
      setOpen(false); setQty(""); setNotes(""); setRepairable(true); onDone && onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className={`inline-flex items-center gap-1 font-bold rounded border border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100 ${compact ? "text-[10px] px-1.5 py-0.5 mt-1" : "text-xs px-2 py-1"}`} data-testid={`rijek-btn-${productId}`}>
        <Scissors className="w-3 h-3" />+ RIJEK
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="rijek-dialog">
          <DialogHeader><DialogTitle className="text-orange-800">Input Rijek Sortir — {wid}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm bg-orange-50 border border-orange-200 rounded px-3 py-2">
              <div className="font-semibold data-raw">{productName} <span className="mono text-slate-500 text-xs">{productId}</span></div>
              <div className="text-xs text-orange-800">Setor efektif saat ini: <b className="mono">{num(setorEfektif ?? 0)} PCS</b>. Rijek akan <b>mengurangi setor</b>, <b>menambah masih di worker</b>, membuka tiket perbaikan, dan memotong ongkos setor asal.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal sortir</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="rijek-date" /></div>
              <div><Label className="text-orange-700">Jumlah rijek (PCS)</Label><Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} className={over ? "border-rose-400" : "border-orange-300"} autoFocus data-testid="rijek-qty" /></div>
            </div>
            {over && <div className="text-xs text-rose-700" data-testid="rijek-over">Melebihi setor efektif {num(setorEfektif)} PCS</div>}
            <div>
              <Label>Jenis rijek</Label>
              <div className="flex rounded-lg bg-slate-100 p-0.5 text-xs font-semibold mt-1" data-testid="rijek-type">
                <button type="button" onClick={() => setRepairable(true)} className={`flex-1 px-2 py-1.5 rounded-md ${repairable ? "bg-white shadow text-emerald-700" : "text-slate-500"}`} data-testid="rijek-type-repair">Bisa diperbaiki (dikembalikan)</button>
                <button type="button" onClick={() => setRepairable(false)} className={`flex-1 px-2 py-1.5 rounded-md ${!repairable ? "bg-white shadow text-rose-700" : "text-slate-500"}`} data-testid="rijek-type-afkir">Afkir (bon barang)</button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{repairable ? "Dibuka tiket perbaikan; bisa dikembalikan setelah diperbaiki." : "Tidak dibuka tiket; masuk potongan bon barang saat Totalan."}</div>
            </div>
            <div><Label>Catatan (opsional)</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="mis. jahitan kerah lepas, ditemukan saat sortir" data-testid="rijek-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="rijek-cancel">Batal</Button>
            <Button onClick={submit} disabled={busy || n <= 0 || over} className="bg-orange-700 hover:bg-orange-800 text-white" data-testid="rijek-submit">{busy ? "Menyimpan…" : `Simpan Rijek ${n > 0 ? num(n) + " PCS" : ""}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
